import {useAccount} from '@/lib/session';
import { useT } from "@/lib/i18n";
import {Dashboard} from '@/components/app/Dashboard';
import { Link } from "@/lib/router";
import { Award, Lock } from "lucide-react";
import { lessons } from "@/data/course";
import { useStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Banner, IconTile, Mascot, ProgressBar, StatBubble, toneCycle } from "@/components/app/Playful";
import { cn } from "@/lib/utils";

/** The learner's own progress: the account dashboard when signed in, otherwise this device's progress. Used by /progress and the leaderboard's "मेरी प्रगति" tab. */
export function MyProgress() { const account=useAccount();return account.user?<Dashboard/>:<Progress/>; }

function Progress() {
  const t = useT();
  const { xp, streak, completed, learned, answered, correct } = useStore();
  const done = Object.keys(completed).length;
  const acc = answered ? Math.round((correct / answered) * 100) : 0;
  const badges = [
    { title: t("प्रथम पाठ", "First lesson"), sub: t("पहला पाठ पूर्ण", "First lesson complete"), got: done >= 1 },
    {
      title: t("सर्वनाम ज्ञाता", "Pronoun pro"),
      sub: t("अध्याय १ पूर्ण", "Chapter 1 complete"),
      got: lessons.filter((l) => l.chapterId === "sarvanam").every((l) => completed[l.id]),
    },
    { title: t("शब्द संग्राहक", "Word collector"), sub: t("10 शब्द सीखे", "Learned 10 words"), got: Object.keys(learned).length >= 10 },
    { title: t("त्रिदिवसीय साधना", "Three-day streak"), sub: t("3 दिन निरंतर", "3 days in a row"), got: streak >= 3 },
    { title: t("शतक", "Century"), sub: "100 XP", got: xp >= 100 },
    { title: t("सटीक साधक", "Sharp learner"), sub: t("90% शुद्धता (20+ उत्तर)", "90% accuracy (20+ answers)"), got: answered >= 20 && acc >= 90 },
  ];
  return (
    <div className="space-y-8">
      <Banner
        tone="dreamy"
        eyebrow={t(t("मेरी प्रगति", "My progress"), "My progress")}
        title={<>{t(t("शाबाश, तुम सितारे हो! ", "Well done, you're a star! "), "Well done, you're a star! ")}<span aria-hidden>⭐</span></>}
        sub={t(t("यह प्रगति इसी डिवाइस पर सहेजी गई है। लॉगिन करके XP और बैज हमेशा के लिए सहेजो।", "This progress is saved on this device only. Log in to keep your XP and badges."), "This progress is saved on this device only. Log in to keep your XP and badges.")}
        art={<Mascot mood="cheer" holding="trophy" className="size-40 sm:size-48" />}
      >
        <Button asChild className="mt-4">
          <Link to="/auth">{t(t("विद्यार्थी लॉगिन", "Log in"), "Log in")}</Link>
        </Button>
      </Banner>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatBubble label={t(t("कुल XP", "Total XP"), "Total XP")} value={xp} emoji="⭐" tone="mango" />
        <StatBubble label={t(t("निरंतरता", "Streak"), "Streak")} value={t(`${streak} दिन`, `${streak} days`)} emoji="🔥" tone="peach" />
        <StatBubble label={t(t("पूर्ण पाठ", "Lessons done"), "Lessons done")} value={`${done} / ${lessons.length}`} emoji="📘" tone="purple" />
        <StatBubble label={t(t("शुद्धता", "Accuracy"), "Accuracy")} value={`${acc}%`} emoji="🎯" tone="mint" />
      </div>
      <section className="card-surface p-6">
        <h2 className="deva mb-4 text-xl font-extrabold">{t(t("पाठ-वार शुद्धता", "Accuracy by lesson"), "Accuracy by lesson")}</h2>
        {done === 0 ? (
          <p className="deva text-muted-foreground">{t(t("अभी कोई पाठ पूर्ण नहीं हुआ।", "No lessons completed yet."), "No lessons completed yet.")}</p>
        ) : (
          <ul className="space-y-3">
            {lessons
              .filter((l) => completed[l.id])
              .map((l) => (
                <li key={l.id}>
                  <div className="deva flex justify-between text-sm font-bold">
                    <span>{l.title}</span>
                    <span>{completed[l.id].accuracy}%</span>
                  </div>
                  <ProgressBar className="mt-1.5" tone="mint" value={completed[l.id].accuracy} max={100} label={`${l.title} %`} />
                </li>
              ))}
          </ul>
        )}
      </section>
      <section>
        <h2 className="deva mb-4 text-xl font-extrabold">{t(t("उपलब्धियाँ 🏅", "Achievements 🏅"), "Achievements 🏅")}</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {badges.map((b, i) => (
            <div
              key={b.title}
              className={cn("bubble flex flex-col items-center gap-2 p-5 text-center", !b.got && "bg-white/60 shadow-none")}
            >
              <IconTile tone={toneCycle[i % toneCycle.length]} size="lg" className={cn("rounded-full", !b.got && "opacity-40 grayscale")}>
                {b.got ? <Award className="size-9 text-white" /> : <Lock className="size-7 text-white" />}
              </IconTile>
              <p className="deva font-extrabold">{b.title}</p>
              <p className="deva text-xs text-muted-foreground">{b.got ? t("मिल गया! 🎉", "Earned! 🎉") : b.sub}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
